import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FilePlus2, LayoutTemplate, Loader2, Plus, Search, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SifaUnterweisung, SifaUnterweisungVorlage } from '../../services/sifa/types';
import { fetchVorlagen } from '../../services/sifa/vorlagenService';
import { describeError } from '../../services/errorMessage';
import {
  LEER_TITEL_VORGABE,
  bereitsAngelegtText,
  filtereVorlagen,
  gruppiereNachKategorie,
  kuerze,
  kurzinfoTeile,
  nummernJeVorlage,
  zaehleKurzinfo,
} from './neueUnterweisungLogic';

/** Der gewaehlte Weg — das Modul legt an, der Dialog sammelt nur die Wahl ein. */
export type AnlegeWeg =
  | { art: 'leer'; titel: string }
  | { art: 'vorlage'; vorlage: SifaUnterweisungVorlage };

interface Props {
  /** Unterweisungen dieses Projekts — fuer „bereits angelegt als Nr. X". */
  unterweisungen: SifaUnterweisung[];
  /** Laeuft das Anlegen gerade (Riegel sitzt im Modul)? Sperrt beide Wege. */
  laeuft?: boolean;
  isReadOnly?: boolean;
  onClose: () => void;
  onAnlegen: (weg: AnlegeWeg) => void;
}

const FELD = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-teal-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/**
 * „Neue Unterweisung" beim Kunden: leer anlegen (nur Titel) oder als Kopie
 * einer freigegebenen Vorlage der Firma (Nr. 141, Plan §3g).
 *
 * Die Vorlagen werden bei JEDEM Oeffnen frisch geladen — wer im Menue
 * „Unterweisungen" gerade freigegeben hat, soll sie hier sofort sehen. Die
 * Auswahl ist zweistufig (antippen, dann „Aus Vorlage anlegen"): ein
 * Fehltipp in einer langen Liste legt sonst eine Unterweisung mit Nummer an,
 * die niemand wollte.
 *
 * Angelegt wird im Modul (onAnlegen) — dort sitzen der Riegel gegen die
 * Doppelanlage und der Wechsel in den Editor.
 */
export const NeueUnterweisungDialog: React.FC<Props> = ({ unterweisungen, laeuft = false, isReadOnly = false, onClose, onAnlegen }) => {
  const [weg, setWeg] = useState<AnlegeWeg['art']>('vorlage');
  const [titel, setTitel] = useState('');
  const [suche, setSuche] = useState('');
  const [gewaehltId, setGewaehltId] = useState<string | null>(null);
  /** null = laedt noch. */
  const [vorlagen, setVorlagen] = useState<SifaUnterweisungVorlage[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    fetchVorlagen({ nurFreigegebene: true })
      .then((liste) => { if (aktiv) setVorlagen(liste); })
      .catch((e) => {
        if (!aktiv) return;
        setLadeFehler(describeError(e));
        setVorlagen([]);
      });
    return () => { aktiv = false; };
  }, []);

  const nummern = useMemo(() => nummernJeVorlage(unterweisungen), [unterweisungen]);
  const gefiltert = useMemo(() => filtereVorlagen(vorlagen ?? [], suche), [vorlagen, suche]);
  const gruppen = useMemo(() => gruppiereNachKategorie(gefiltert), [gefiltert]);
  // Nur eine sichtbare Wahl zaehlt: was die Suche ausblendet, wird nicht angelegt.
  const gewaehlt = gefiltert.find((v) => v.id === gewaehltId) ?? null;

  const gesperrt = isReadOnly || laeuft;
  const kannAnlegen = !gesperrt && (weg === 'leer' || !!gewaehlt);

  const anlegen = () => {
    if (!kannAnlegen) return;
    if (weg === 'leer') onAnlegen({ art: 'leer', titel });
    else if (gewaehlt) onAnlegen({ art: 'vorlage', vorlage: gewaehlt });
  };

  // Waehrend des Anlegens nicht wegklicken: der Editor oeffnet gleich ohnehin.
  const schliessen = () => { if (!laeuft) onClose(); };

  const reiter = (art: AnlegeWeg['art'], Icon: LucideIcon, text: string) => (
    <button
      type="button"
      onClick={() => setWeg(art)}
      disabled={laeuft}
      className={`flex-1 min-h-[40px] px-3 flex items-center justify-center gap-1.5 text-xs font-bold rounded-lg transition-colors disabled:opacity-40 ${
        weg === art ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {text}
    </button>
  );

  const vorlagenListe = () => {
    if (vorlagen === null) {
      return <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>;
    }
    if (ladeFehler) {
      return (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>Die Vorlagen ließen sich nicht laden: {ladeFehler}</span>
        </div>
      );
    }
    if (vorlagen.length === 0) {
      return (
        <div className="p-6 bg-white border border-dashed border-slate-300 rounded-2xl text-center space-y-2">
          <p className="text-sm text-slate-600 font-semibold">Noch keine freigegebenen Vorlagen</p>
          <p className="text-xs text-slate-500">
            Vorlagen legen Admins und Nutzer mit Häkchen „SiFa" im Menü „Unterweisungen" an und geben sie dort frei.
            Bis dahin lässt sich die Unterweisung leer anlegen.
          </p>
          <button
            type="button"
            onClick={() => setWeg('leer')}
            className="min-h-[40px] px-4 inline-flex items-center gap-2 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-xl"
          >
            <FilePlus2 className="w-4 h-4" /> Leer anlegen
          </button>
        </div>
      );
    }
    return (
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Suchen nach Titel, Kategorie, Beschreibung"
            className={`${FELD} pl-9`}
            autoFocus
          />
        </div>
        {gruppen.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-6">Keine Vorlage passt zu „{suche.trim()}".</p>
        ) : (
          gruppen.map((g) => (
            <div key={g.kategorie} className="space-y-1.5">
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-400">
                {g.kategorie} <span className="font-semibold">({g.vorlagen.length})</span>
              </p>
              {g.vorlagen.map((v) => {
                const info = kurzinfoTeile(zaehleKurzinfo(v)).join(' · ');
                const schon = bereitsAngelegtText(nummern.get(v.id));
                const aktiv = v.id === gewaehlt?.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setGewaehltId(v.id)}
                    disabled={laeuft}
                    aria-pressed={aktiv}
                    className={`w-full text-left p-3 rounded-xl border transition-colors disabled:opacity-60 ${
                      aktiv ? 'border-teal-400 bg-teal-50 ring-1 ring-teal-300' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <p className="text-sm font-bold text-slate-800">{v.titel || 'ohne Titel'}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{info}</p>
                    {v.beschreibung && <p className="text-xs text-slate-600 mt-1">{kuerze(v.beschreibung)}</p>}
                    {schon && (
                      <span className="mt-1.5 inline-flex items-center px-2 py-0.5 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full">
                        {schon}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={schliessen}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
          <h3 className="text-sm font-bold text-slate-800">Neue Unterweisung</h3>
          <button onClick={schliessen} disabled={laeuft} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
        </div>

        <div className="p-4 pb-0 shrink-0">
          <div className="flex gap-1 p-1 bg-slate-100 rounded-xl">
            {reiter('vorlage', LayoutTemplate, 'Aus Vorlage')}
            {reiter('leer', FilePlus2, 'Leer anlegen')}
          </div>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          {isReadOnly && (
            <p className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
              Nur-Ansicht: In diesem Projekt wird keine Unterweisung angelegt.
            </p>
          )}
          {weg === 'leer' ? (
            <div>
              <label className={LABEL}>Titel</label>
              <input
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }}
                disabled={gesperrt}
                placeholder={`z. B. Brandschutz — leer bleibt „${LEER_TITEL_VORGABE}"`}
                maxLength={200}
                className={FELD}
                autoFocus
              />
              <p className="text-[11px] text-slate-500 mt-1">Abschnitte und Fragen folgen im Editor, der sich gleich öffnet.</p>
            </div>
          ) : (
            vorlagenListe()
          )}
        </div>

        {!isReadOnly && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 shrink-0">
            {weg === 'vorlage' && gewaehlt && (
              <p className="mr-auto text-[11px] text-slate-500 truncate min-w-0">Gewählt: {gewaehlt.titel}</p>
            )}
            <button onClick={schliessen} disabled={laeuft} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">Abbrechen</button>
            <button
              onClick={anlegen}
              disabled={!kannAnlegen}
              className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 rounded-xl disabled:opacity-40"
            >
              {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {weg === 'leer' ? 'Leer anlegen' : 'Aus Vorlage anlegen'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
